/**
 * InsiderMonitor — compliance panel surfaced on the RCM CIF record page.
 *
 * Reads the customer's Core Director `InsiderCode` (Field 21 — Employee Code)
 * and renders an amber advisory Banner whenever the value is non-zero (the
 * customer is flagged as an employee, director, or special-consideration
 * relationship). The insider code map is the one confirmed in
 * `memory/core-director-cif-fields.md`:
 *
 *     0       — Non-employee (default; no banner)
 *     1 – 5   — Employee Restricted (bank management controls access)
 *     6 – 9   — Employee Non-Restricted (directors / other-bank employees)
 *
 * `CustInq` does NOT always populate `x_BusDetail` (per
 * `memory/custinq-limitations.md`), so we default to `'0'` when the server
 * omits the field. When the real SQL-seeded `CoreDirectorCodes` table lands
 * the caller can pass an override description via `codeMap`; until then the
 * hard-coded labels below ship with the port.
 *
 * Ported from legacy `services/InsiderMonitor.ts` (fraud-alert cross-reference)
 * but intentionally simplified for the demo: we surface the insider status
 * visibly. The employee-CIF cache + cross-field match is out of scope for
 * this wave — the legacy service hit `ao_InsiderCache` which Bible §3.0
 * retires. Re-port as a server-side `/v3/insider/check` call in a later wave.
 */
import { Banner, Pill } from '@/components/primitives';

export interface InsiderMonitorProps {
  /** Raw `InsiderCode` string from jXRL CustInq (or undefined). */
  insiderCode: string | undefined;
  /** Optional override description lookup (future: from SQL CoreDirectorCodes). */
  codeMap?: Readonly<Record<string, string>>;
  /** Controls visual density — compact for RCM landing; full for CIF detail. */
  variant?: 'compact' | 'full';
}

/** Field 21 labels, Core Director's own legend (Bill 2026-10-17): 0=Non-employee, 1-5=Employee Restricted, 6-9=Emp. Not Restr. */
const DEFAULT_LABELS: Readonly<Record<string, string>> = {
  '0': 'Non-employee',
  '1': 'Employee Restricted', '2': 'Employee Restricted', '3': 'Employee Restricted', '4': 'Employee Restricted', '5': 'Employee Restricted',
  '6': 'Employee Not Restricted', '7': 'Employee Not Restricted', '8': 'Employee Not Restricted', '9': 'Employee Not Restricted',
};

/**
 * Resolve the display label — caller override beats default; empty / unknown
 * codes render as `"Unknown (<code>)"` rather than the bare number.
 */
function resolveLabel(
  code: string,
  codeMap?: Readonly<Record<string, string>>,
): string {
  if (codeMap && codeMap[code]) return codeMap[code];
  if (DEFAULT_LABELS[code]) return DEFAULT_LABELS[code];
  return `Unknown (${code})`;
}

export function InsiderMonitor({
  insiderCode,
  codeMap,
  variant = 'full',
}: InsiderMonitorProps): JSX.Element {
  // Blank = not read from Core Director (never shown as 0 — Bill 2026-10-17: an employee showed as Non-Employee).
  const code = (insiderCode ?? '').trim() || '?';
  const label = code === '?' ? 'not read from Core Director' : resolveLabel(code, codeMap);
  const isInsider = code !== '0' && code !== '?';
  const restricted = code >= '1' && code <= '5';

  if (variant === 'compact') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          fontSize: 12,
        }}
      >
        {/* Bill 2026-07-13 r2 / BACKLOG-0869: this is the CIF-level Employee
            Code (Core Director Field 21), NOT a per-account BSA "insider"
            flag — the old "Insider" label conflated the two. Labeled
            accurately here; the per-account insider determination is a
            separate follow-up on the Products tab. */}
        <strong style={{ color: 'var(--ink-700)' }} title="Core Director Field 21 — CIF Employee Code">Employee (F21)</strong>
        <Pill variant={isInsider ? 'warn' : 'muted'}>{code}</Pill>
        <span style={{ color: 'var(--ink-600)' }}>{label}</span>
      </span>
    );
  }

  return (
    <section
      aria-label="Insider compliance status"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <strong
          style={{
            fontSize: 11,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: 'var(--ink-700)',
          }}
        >
          Employee Code (F21)
        </strong>
        <Pill variant={isInsider ? 'warn' : 'ok'}>{code}</Pill>
        <span style={{ fontSize: 13, color: 'var(--ink-900)' }}>{label}</span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--ink-500)',
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
          }}
          title="Core Director Field 21 — Employee Code"
        >
          Field 21
        </span>
      </div>

      {isInsider && (
        <Banner
          tone="warn"
          title="Insider flag on file — review before making changes"
        >
          This customer is flagged as {restricted ? 'an employee with restricted access' : 'a director / non-restricted employee'}.
          Per PCB insider-trading policy, any CIF modification (address, phone,
          email, beneficiary) requires secondary review. A fraud-alert task is
          auto-generated on contact-field CustMod until the dedicated insider
          check endpoint ships.
        </Banner>
      )}
    </section>
  );
}
